import React from 'react';
import { Link } from 'react-router-dom';
import { useCompare } from '../contexts/CompareContext';
import { Button } from './ui/button';
import { X, GitCompare, Trash2 } from 'lucide-react';

export function CompareBar() {
  const { compareList, removeFromCompare, clearCompare } = useCompare();

  if (compareList.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-sm border-t border-slate-700 shadow-2xl">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Title and count */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-emerald-400">
              <GitCompare className="w-5 h-5" />
              <span className="font-semibold">Comparar</span>
              <span className="bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full">
                {compareList.length}
              </span>
            </div>
          </div>

          {/* Center: Selected centers */}
          <div className="flex-1 flex items-center gap-2 overflow-x-auto scrollbar-hide">
            {compareList.map((center) => (
              <div
                key={center.center_id}
                className="flex items-center gap-2 bg-slate-800 rounded-lg px-3 py-1.5 min-w-fit"
              >
                <span className="text-slate-200 text-sm truncate max-w-[150px]">
                  {center.name}
                </span>
                <button
                  onClick={() => removeFromCompare(center.center_id)}
                  className="text-slate-400 hover:text-red-400 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={clearCompare}
              className="text-slate-400 hover:text-red-400"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            <Link to={`/comparar?ids=${compareList.map(c => c.center_id).join(',')}`}>
              <Button 
                size="sm" 
                className="bg-emerald-500 hover:bg-emerald-600"
                disabled={compareList.length < 2}
              >
                <GitCompare className="w-4 h-4 mr-2" />
                Ver Comparación
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CompareCheckbox({ center, className = '' }) {
  const { isInCompare, toggleCompare, canAddMore } = useCompare();
  const isSelected = isInCompare(center.center_id);
  const disabled = !isSelected && !canAddMore;

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      toggleCompare(center);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={disabled}
      className={`
        flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-all
        ${isSelected 
          ? 'bg-emerald-500 text-white' 
          : disabled 
            ? 'bg-slate-700 text-slate-500 cursor-not-allowed' 
            : 'bg-slate-700/80 text-slate-300 hover:bg-slate-600'
        }
        ${className}
      `}
      title={disabled ? 'Máximo 4 centros para comparar' : isSelected ? 'Quitar de comparación' : 'Agregar a comparación'}
    >
      <GitCompare className="w-3 h-3" />
      {isSelected ? 'Comparando' : 'Comparar'}
    </button>
  );
}

export default CompareBar;
